import { Controller } from "@hotwired/stimulus"
import { ICON_BG, ICON_COLORS, NO_BRAND_TILE, applyIconTileStyle, categoryLabel, iconSvgHtml, noBrandSvgHtml } from "../helpers/expense_constants"
import { intlLocale } from "../helpers/locale"

/**
 * Expense Edit Modal — dashboard inline editor.
 *
 * Mounted on the phone card shell div alongside voice-modal and confirm-sheet.
 * Opens a bottom-sheet with the full edit form pre-populated from JSON data
 * attributes on the expense row — zero server request on open.
 */
export default class extends Controller {
  static targets = [
    "backdrop", "sheet",
    "errorArea", "errorText",
    "amountField", "currencyLabel", "nameField",
    "categoryField", "iconField",
    "categoryTriggerIcon", "categoryTriggerLabel",
    "categoryPickerOverlay", "categoryGridItem",
    "iconTriggerIcon", "iconTriggerLabel",
    "iconPickerOverlay", "iconClearButton",
    "brandIconField", "brandResults", "brandEmptyState",
    "pickerSelectedRow", "pickerSelectedTile", "pickerSelectedLabel",
    "pickerCategorySelectedTile", "pickerCategorySelectedLabel",
    "frequencyPill", "freqSlider", "freqTrack",
    "recurringToggle", "recurringKnob",
    "startDateSection", "startDateField",
    "renewalCard", "renewalText"
  ]

  static values = {
    renewsTemplate: { type: String, default: "Renews: %{date}" },
    defaultCurrency: { type: String, default: "JOD" },
    brandSearchUrl: { type: String, default: "" },
    i18n: Object
  }

  // ── Modal open / close ──────────────────────────────────────────────────

  open(event) {
    const params = event.params || {}
    const json = params.expense
    if (!json) return

    this._expense = typeof json === "string" ? JSON.parse(json) : { ...json }
    this._updateUrl = params.updateUrl
    this._deleteUrl = params.deleteUrl
    this._deleteTitle = params.deleteTitle || ""
    this._deleteMessage = params.deleteMessage || ""

    this._populateForm()
    this._hideErrors()

    // Show modal
    this.sheetTarget.classList.remove("hidden")
    this.backdropTarget.classList.remove("hidden")
    this.backdropTarget.classList.remove("opacity-0", "pointer-events-none")
    this.sheetTarget.style.transition = "transform 320ms cubic-bezier(0.32, 0.72, 0, 1)"
    this.sheetTarget.offsetHeight // force reflow
    this.sheetTarget.classList.remove("translate-y-full")
    this.sheetTarget.classList.add("translate-y-0")
  }

  close() {
    this.backdropTarget.classList.add("opacity-0", "pointer-events-none")
    this.sheetTarget.style.transition = "transform 220ms cubic-bezier(0.4, 0, 1, 1)"
    this.sheetTarget.classList.remove("translate-y-0")
    this.sheetTarget.classList.add("translate-y-full")

    // Close category picker if open
    if (this.hasCategoryPickerOverlayTarget) {
      this.categoryPickerOverlayTarget.classList.add("hidden")
      this.categoryPickerOverlayTarget.classList.remove("category-picker-enter", "category-picker-exit")
    }
    // Close icon picker if open
    if (this.hasIconPickerOverlayTarget) {
      this.iconPickerOverlayTarget.classList.add("hidden")
    }
  }

  backdropClose(event) {
    if (event.target === this.backdropTarget) this.close()
  }

  // ── Save ────────────────────────────────────────────────────────────────
  // Using requestSubmit() instead of fetch() ensures the response goes through
  // Turbo's native adapter — critical for iOS Hotwire Native where fetch()
  // bypasses the native pipeline and the snapshot cache is never invalidated.

  save(event) {
    event.preventDefault()
    if (!this._updateUrl) return

    const expense = {
      name: this.nameFieldTarget.value.trim(),
      amount: this.amountFieldTarget.value.trim(),
      expense_type: this._expense?.expense_type || "consuming",
      frequency: this._editFrequency || "monthly",
      recurring: this._editRecurring,
      category: this._editCategory || "other",
      icon: this._editIcon || "",
      brand_icon: this._editBrandIcon || "",
      currency: this._expense.currency || this.defaultCurrencyValue,
      start_date: this.hasStartDateFieldTarget ? this.startDateFieldTarget.value : null
    }

    // Client-side validation
    if (!expense.name || !expense.amount || parseFloat(expense.amount) <= 0) {
      this._showErrors([this._t("save_failed")])
      return
    }

    this.close()

    // Force-hide modal after close animation completes
    setTimeout(() => {
      if (this.hasSheetTarget) this.sheetTarget.classList.add("hidden")
      if (this.hasBackdropTarget) this.backdropTarget.classList.add("hidden")
    }, 350)

    this._submitUpdateForm(expense)
  }

  _submitUpdateForm(expense) {
    const form = document.createElement("form")
    form.method = "POST"
    form.action = this._updateUrl
    form.style.display = "none"

    // Rails method override for PATCH
    const methodInput = document.createElement("input")
    methodInput.type = "hidden"
    methodInput.name = "_method"
    methodInput.value = "patch"
    form.appendChild(methodInput)

    // CSRF token
    const csrf = document.querySelector("meta[name='csrf-token']")?.content
    if (csrf) {
      const csrfInput = document.createElement("input")
      csrfInput.type = "hidden"
      csrfInput.name = "authenticity_token"
      csrfInput.value = csrf
      form.appendChild(csrfInput)
    }

    // Expense fields as expense[key] for Rails strong parameters
    for (const [key, value] of Object.entries(expense)) {
      if (value != null) {
        const input = document.createElement("input")
        input.type = "hidden"
        input.name = `expense[${key}]`
        input.value = String(value)
        form.appendChild(input)
      }
    }

    document.body.appendChild(form)

    // Clean up form after Turbo processes the response
    const cleanup = () => { if (form.parentNode) form.remove() }
    form.addEventListener("turbo:submit-end", cleanup, { once: true })
    setTimeout(cleanup, 10000) // safety net

    form.requestSubmit()
  }

  // ── Delete ──────────────────────────────────────────────────────────────

  deleteExpense() {
    if (!this._deleteUrl) return

    this.close()

    // Open confirm-sheet programmatically after modal closes
    setTimeout(() => {
      const shell = this.element
      const confirmSheet = this.application.getControllerForElementAndIdentifier(shell, "confirm-sheet")
      if (confirmSheet) {
        confirmSheet.open({
          params: {
            url: this._deleteUrl,
            title: this._deleteTitle,
            message: this._deleteMessage
          }
        })
      }
    }, 250)
  }

  // ── Frequency — sliding segmented control ─────────────────────────────

  selectFrequency(event) {
    this._editFrequency = event.currentTarget.dataset.freq
    this._syncFrequencyPills()
    this._updateRenewalPreview()
  }

  _syncFrequencyPills() {
    const freqs = ["daily", "weekly", "monthly", "yearly"]
    const activeIdx = freqs.indexOf(this._editFrequency)
    const isRecurring = this._editRecurring
    const slider = this.hasFreqSliderTarget ? this.freqSliderTarget : null

    this._slideIndicator(slider, this.frequencyPillTargets, Math.max(0, activeIdx))

    if (slider) {
      slider.style.opacity = isRecurring ? "1" : "0"
    }
    if (this.hasFreqTrackTarget) {
      this.freqTrackTarget.classList.toggle("pointer-events-none", !isRecurring)
    }

    this.frequencyPillTargets.forEach(pill => {
      const active = pill.dataset.freq === this._editFrequency
      pill.classList.remove("text-slate-900", "text-slate-400", "text-slate-300")
      if (!isRecurring) {
        pill.classList.add("text-slate-300")
      } else {
        pill.classList.add(active ? "text-slate-900" : "text-slate-400")
      }
    })
  }

  // ── Recurring toggle ──────────────────────────────────────────────────

  toggleRecurring() {
    this._editRecurring = !this._editRecurring
    this._syncRecurringToggle()
  }

  _syncRecurringToggle() {
    if (!this.hasRecurringToggleTarget) return
    const isRTL = document.documentElement.dir === "rtl"
    const isRecurring = this._editRecurring

    this.recurringToggleTarget.classList.toggle("bg-blue-600", isRecurring)
    this.recurringToggleTarget.classList.toggle("bg-slate-300", !isRecurring)

    if (this.hasRecurringKnobTarget) {
      const onTranslate = isRTL ? "-translate-x-[22px]" : "translate-x-[22px]"
      const offTranslate = isRTL ? "-translate-x-0.5" : "translate-x-0.5"
      this.recurringKnobTarget.className =
        "inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200"
      this.recurringKnobTarget.classList.add(isRecurring ? onTranslate : offTranslate)
    }

    this._syncFrequencyPills()
    this._syncStartDateSection()
    this._updateRenewalPreview()
  }

  _syncStartDateSection() {
    if (!this.hasStartDateSectionTarget) return
    this.startDateSectionTarget.classList.toggle("hidden", !this._editRecurring)
  }

  onStartDateChange() {
    this._updateRenewalPreview()
  }

  // ── Category picker ───────────────────────────────────────────────────

  openCategoryPicker() {
    if (!this.hasCategoryPickerOverlayTarget) return
    const overlay = this.categoryPickerOverlayTarget
    overlay.classList.remove("hidden", "category-picker-exit")
    overlay.classList.add("category-picker-enter")
    this._syncCategoryGrid()
    this._resetCategoryFilter()
  }

  filterCategories(e) {
    const q = (e.target.value || "").trim().toLowerCase()
    const overlay = this.categoryPickerOverlayTarget
    overlay.querySelectorAll("[data-cat]").forEach(btn => {
      const label = (btn.dataset.label || btn.textContent || "").toLowerCase()
      btn.classList.toggle("hidden", q.length > 0 && !label.includes(q))
    })
    overlay.querySelectorAll(".grid").forEach(grid => {
      const anyVisible = Array.from(grid.children).some(c => !c.classList.contains("hidden"))
      grid.classList.toggle("hidden", !anyVisible)
      const header = grid.previousElementSibling
      if (header) header.classList.toggle("hidden", !anyVisible)
    })
  }

  _resetCategoryFilter() {
    if (!this.hasCategoryPickerOverlayTarget) return
    const overlay = this.categoryPickerOverlayTarget
    const input = overlay.querySelector("[data-category-search]")
    if (input) input.value = ""
    overlay.querySelectorAll(".hidden[data-cat], .grid.hidden").forEach(el => el.classList.remove("hidden"))
    overlay.querySelectorAll(".grid").forEach(grid => {
      const header = grid.previousElementSibling
      if (header) header.classList.remove("hidden")
    })
  }

  closeCategoryPicker() {
    if (!this.hasCategoryPickerOverlayTarget) return
    const overlay = this.categoryPickerOverlayTarget
    overlay.classList.remove("category-picker-enter")
    overlay.classList.add("category-picker-exit")
    const handler = () => {
      overlay.classList.add("hidden")
      overlay.classList.remove("category-picker-exit")
      overlay.removeEventListener("animationend", handler)
    }
    overlay.addEventListener("animationend", handler)
  }

  selectCategoryFromPicker(event) {
    this._editCategory = event.currentTarget.dataset.cat
    this._syncCategoryTrigger()
    this._syncCategoryGrid()
    this._syncCategoryHeroInPicker()
    setTimeout(() => this.closeCategoryPicker(), 180)
  }

  _syncCategoryHeroInPicker() {
    const cat = this._editCategory || "other"
    if (this.hasPickerCategorySelectedTileTarget) {
      this.pickerCategorySelectedTileTarget.innerHTML = `
        <div class="expense-icon-tile flex items-center justify-center"
             style="width:38px;height:38px;border-radius:16px;flex-shrink:0;
                    --c-light:${(ICON_COLORS[cat] || ICON_COLORS.other).light};
                    --c-dark:${(ICON_COLORS[cat] || ICON_COLORS.other).dark};">
          ${iconSvgHtml(cat, 22)}
        </div>
      `
    }
    if (this.hasPickerCategorySelectedLabelTarget) {
      this.pickerCategorySelectedLabelTarget.textContent = categoryLabel(cat)
    }
  }

  _syncCategoryTrigger() {
    const cat = this._editCategory || "other"
    if (this.hasCategoryTriggerIconTarget) {
      this.categoryTriggerIconTarget.innerHTML = iconSvgHtml(cat, 28)
      applyIconTileStyle(this.categoryTriggerIconTarget, cat)
    }
    if (this.hasCategoryTriggerLabelTarget) {
      this.categoryTriggerLabelTarget.textContent = categoryLabel(cat)
    }
  }

  _syncCategoryGrid() {
    this.categoryGridItemTargets.forEach(item => {
      const active = item.dataset.cat === this._editCategory
      item.classList.toggle("ring-2", active)
      item.classList.toggle("ring-blue-500", active)
      item.classList.toggle("ring-offset-2", active)
      item.classList.toggle("bg-blue-50/50", active)
    })
  }

  // ── Icon picker ───────────────────────────────────────────────────────

  openIconPicker() {
    if (!this.hasIconPickerOverlayTarget) return
    this.iconPickerOverlayTarget.classList.remove("hidden")
    this._resetIconFilter()
    // No fetch on open — popular brands are pre-rendered server-side.
  }

  closeIconPicker() {
    if (!this.hasIconPickerOverlayTarget) return
    this.iconPickerOverlayTarget.classList.add("hidden")
  }

  _resetIconFilter() {
    const overlay = this.iconPickerOverlayTarget
    const input = overlay.querySelector("[data-icon-search]")
    if (input) input.value = ""
    if (this.hasBrandEmptyStateTarget) this.brandEmptyStateTarget.classList.add("hidden")
  }

  filterIcons(e) {
    this._queueBrandSearch((e.target.value || "").trim())
  }

  _queueBrandSearch(q) {
    if (this._brandTimer) clearTimeout(this._brandTimer)
    if (!this.brandSearchUrlValue) return
    this._brandTimer = setTimeout(() => this._fetchBrands(q), 200)
  }

  async _fetchBrands(q) {
    try {
      const r = await fetch(`${this.brandSearchUrlValue}?q=${encodeURIComponent(q)}`,
                            { headers: { Accept: "application/json" } })
      if (!r.ok) return
      const data = await r.json()
      this._renderBrands(data.brands || [])
    } catch (_) { /* ignore */ }
  }

  _renderBrands(brands) {
    if (!this.hasBrandResultsTarget) return
    const target = this.brandResultsTarget
    target.innerHTML = ""
    if (!brands.length) {
      if (this.hasBrandEmptyStateTarget) this.brandEmptyStateTarget.classList.remove("hidden")
      return
    }
    if (this.hasBrandEmptyStateTarget) this.brandEmptyStateTarget.classList.add("hidden")
    brands.forEach(b => {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.className = "flex flex-col items-center gap-1.5 p-1.5 rounded-2xl active:scale-95 transition"
      btn.dataset.brandSlug = b.slug
      btn.dataset.brandTitle = b.title
      btn.dataset.brandHex = b.hex || ""
      btn.dataset.action = "click->expense-modal#selectBrandIcon"
      const hex = b.hex || "94A3B8"
      const tile = document.createElement("div")
      tile.className = "flex items-center justify-center"
      tile.setAttribute("data-brand-tile", "")
      tile.style.cssText = `width:48px;height:48px;border-radius:14px;background:#${hex};flex-shrink:0;`
      let svg = b.svg || ""
      svg = svg.replace(/<svg\b/, '<svg width="28" height="28" fill="#FFFFFF" style="fill:#FFFFFF"')
      tile.innerHTML = svg
      const label = document.createElement("span")
      label.className = "text-[11px] font-bold text-base-content/70 text-center truncate max-w-[64px]"
      label.textContent = b.title
      btn.appendChild(tile)
      btn.appendChild(label)
      target.appendChild(btn)
    })
  }

  selectBrandIcon(event) {
    const btn = event.currentTarget
    this._editBrandIcon = btn.dataset.brandSlug || ""
    this._editIcon = ""
    if (this.hasBrandIconFieldTarget) this.brandIconFieldTarget.value = this._editBrandIcon
    if (this.hasIconFieldTarget) this.iconFieldTarget.value = ""
    this._syncIconTrigger()

    // In-picker hero card with current pick
    const brandTile = btn.querySelector("[data-brand-tile]")
    const title = btn.dataset.brandTitle || this._editBrandIcon
    if (this.hasPickerSelectedRowTarget) this.pickerSelectedRowTarget.classList.remove("hidden")
    if (this.hasPickerSelectedTileTarget && brandTile) {
      const clone = brandTile.cloneNode(true)
      clone.style.width = "38px"; clone.style.height = "38px"; clone.style.borderRadius = "16px"
      const svg = clone.querySelector("svg")
      if (svg) { svg.setAttribute("width", "22"); svg.setAttribute("height", "22") }
      this.pickerSelectedTileTarget.innerHTML = ""
      this.pickerSelectedTileTarget.appendChild(clone)
    }
    if (this.hasPickerSelectedLabelTarget) this.pickerSelectedLabelTarget.textContent = title

    this._updateBrandGridRing(this._editBrandIcon)

    setTimeout(() => this.closeIconPicker(), 180)
  }

  _updateBrandGridRing(activeSlug) {
    if (!this.hasBrandResultsTarget) return
    this.brandResultsTarget.querySelectorAll("[data-brand-slug]").forEach(btn => {
      const wrap = btn.querySelector("[data-brand-tile]")?.parentElement
      if (!wrap) return
      const active = btn.dataset.brandSlug === activeSlug
      wrap.classList.toggle("ring-2", active)
      wrap.classList.toggle("ring-indigo-400", active)
      wrap.classList.toggle("ring-offset-2", active)
      wrap.classList.toggle("rounded-[20px]", active)
      wrap.classList.toggle("p-[2px]", active)
    })
  }

  clearIcon(e) {
    if (e && typeof e.preventDefault === "function") e.preventDefault()
    this._editIcon = ""
    this._editBrandIcon = ""
    if (this.hasIconFieldTarget) this.iconFieldTarget.value = ""
    if (this.hasBrandIconFieldTarget) this.brandIconFieldTarget.value = ""
    this._syncIconTrigger()
    if (this.hasPickerSelectedRowTarget) this.pickerSelectedRowTarget.classList.add("hidden")
    this._updateBrandGridRing(null)
    this.closeIconPicker()
  }

  _syncIconTrigger() {
    const brand = this._editBrandIcon
    if (this.hasIconTriggerIconTarget) {
      if (brand) {
        // Brand SVGs aren't bundled in JS; show a monogram tile until the next
        // selectBrandIcon call replaces it with the real cloned tile.
        const initial = brand.charAt(0).toUpperCase()
        this.iconTriggerIconTarget.innerHTML =
          `<span style="font-size:16px;font-weight:800;color:#fff;">${initial}</span>`
        this.iconTriggerIconTarget.style.background = "#475569"
        this.iconTriggerIconTarget.style.removeProperty("--c-light")
        this.iconTriggerIconTarget.style.removeProperty("--c-dark")
      } else {
        // No brand → the storefront no-brand tile, matches the form trigger.
        this.iconTriggerIconTarget.innerHTML = noBrandSvgHtml(22)
        this.iconTriggerIconTarget.style.background = ""
        this.iconTriggerIconTarget.style.setProperty("--c-light", NO_BRAND_TILE.light)
        this.iconTriggerIconTarget.style.setProperty("--c-dark", NO_BRAND_TILE.dark)
        this.iconTriggerIconTarget.classList.add("expense-icon-tile")
      }
    }
    if (this.hasIconTriggerLabelTarget) {
      this.iconTriggerLabelTarget.textContent = brand
        ? brand.replace(/[-_]/g, " ").replace(/\b\w/g, c => c.toUpperCase())
        : categoryLabel("other")
    }
    if (this.hasIconClearButtonTarget) {
      this.iconClearButtonTarget.classList.toggle("hidden", !brand)
    }
  }

  // ── Sliding pill core ─────────────────────────────────────────────────

  _slideIndicator(slider, buttons, activeIndex) {
    if (!slider) return
    const isRTL = document.documentElement.dir === "rtl"
    const pct = activeIndex * 100
    slider.style.transform = `translateX(${isRTL ? -pct : pct}%)`
  }

  // ── Form population ───────────────────────────────────────────────────

  _populateForm() {
    const d = this._expense

    this.nameFieldTarget.value = d.name || ""
    this.amountFieldTarget.value = d.amount || "0"

    const currencyCode = d.currency || this.defaultCurrencyValue
    const currencies = (this.hasI18nValue && this.i18nValue.currencies) || {}
    this.currencyLabelTarget.textContent = currencies[currencyCode] || currencyCode

    this._editFrequency = d.frequency || "monthly"
    this._editRecurring = d.recurring !== false
    this._editCategory = d.category || "other"
    this._editIcon = d.icon || ""
    this._editBrandIcon = d.brand_icon || ""

    if (this.hasStartDateFieldTarget) {
      this.startDateFieldTarget.value = d.start_date || ""
    }

    if (this.hasCategoryFieldTarget) this.categoryFieldTarget.value = this._editCategory
    if (this.hasIconFieldTarget) this.iconFieldTarget.value = this._editIcon || ""
    if (this.hasBrandIconFieldTarget) this.brandIconFieldTarget.value = this._editBrandIcon || ""

    this._syncFrequencyPills()
    this._syncRecurringToggle()
    this._syncCategoryTrigger()
    this._syncCategoryHeroInPicker()
    this._syncIconTrigger()
    this._syncStartDateSection()
    this._updateRenewalPreview()
  }

  // ── Renewal preview ───────────────────────────────────────────────────

  _updateRenewalPreview() {
    if (!this.hasRenewalCardTarget) return

    if (!this._editRecurring) {
      this.renewalCardTarget.classList.add("hidden")
      return
    }
    const startDate = this.hasStartDateFieldTarget ? this.startDateFieldTarget.value : null
    const renewal = this._computeNextRenewal(startDate, this._editFrequency)
    if (renewal) {
      const tmpl = this.renewsTemplateValue
      this.renewalTextTarget.textContent = tmpl.replace("%{date}", this._formatDate(renewal))
      this.renewalCardTarget.classList.remove("hidden")
      this.renewalCardTarget.classList.remove("renewal-pulse")
      void this.renewalCardTarget.offsetWidth
      this.renewalCardTarget.classList.add("renewal-pulse")
    } else {
      this.renewalCardTarget.classList.add("hidden")
    }
  }

  _computeNextRenewal(startDateISO, frequency) {
    if (!startDateISO || !frequency) return null
    const start = new Date(startDateISO + "T00:00:00")
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const advance = (d) => {
      switch (frequency) {
        case "daily":   d.setDate(d.getDate() + 1); break
        case "weekly":  d.setDate(d.getDate() + 7); break
        case "monthly": d.setMonth(d.getMonth() + 1); break
        case "yearly":  d.setFullYear(d.getFullYear() + 1); break
        default:        d.setMonth(d.getMonth() + 1)
      }
    }

    // Start date = first occurrence. Renewal = next occurrence after start.
    let candidate = new Date(start)
    advance(candidate)
    while (candidate < today) { advance(candidate) }

    const y = candidate.getFullYear()
    const m = String(candidate.getMonth() + 1).padStart(2, '0')
    const d = String(candidate.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
  }

  _formatDate(isoString) {
    if (!isoString) return ""
    const date = new Date(isoString + "T00:00:00")
    return date.toLocaleDateString(intlLocale(), {
      year: "numeric", month: "long", day: "numeric"
    })
  }

  // ── Error display ─────────────────────────────────────────────────────

  _showErrors(errors) {
    if (!this.hasErrorAreaTarget) return
    if (this.hasErrorTextTarget) {
      this.errorTextTarget.textContent = errors.join(", ")
    }
    this.errorAreaTarget.classList.remove("hidden")
    this.errorAreaTarget.classList.remove("voice-fade-in")
    void this.errorAreaTarget.offsetWidth
    this.errorAreaTarget.classList.add("voice-fade-in")
  }

  _hideErrors() {
    if (this.hasErrorAreaTarget) this.errorAreaTarget.classList.add("hidden")
  }

  dismissError() {
    this._hideErrors()
  }

  // ── i18n helper ───────────────────────────────────────────────────────

  _t(key) {
    if (this.hasI18nValue && this.i18nValue[key]) return this.i18nValue[key]
    return key
  }
}
